import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import api from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pnc_user") || "null"); }
    catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("pnc_token", data.token);
      localStorage.setItem("pnc_user", JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("pnc_token");
    localStorage.removeItem("pnc_user");
    setUser(null);
  };

  // On mount: pull fresh user from server (/auth/me). This catches the case
  // where the admin flag was flipped off in the DB after the user signed in —
  // their cached localStorage would still say is_admin=true but the server
  // now disagrees. The axios 403 interceptor also flips is_admin to false in
  // cache on an "Admin only" response so RequireAuth can bounce them.
  useEffect(() => {
    if (!user) return;
    api.get("/auth/me").then(({ data }) => {
      // Detect a privilege downgrade and warn once, so users understand why
      // admin-only buttons stop working mid-session instead of showing "Admin only".
      try {
        const cached = JSON.parse(localStorage.getItem("pnc_user") || "null");
        if (cached && cached.is_admin && !data.is_admin) {
          // Lazy import to avoid circular dep at module load.
          import("sonner").then(({ toast }) => toast.error(
            "Your admin access was revoked. Reloading…", { duration: 4000 }
          ));
          setTimeout(() => window.location.reload(), 1200);
        }
      } catch { /* ignore */ }
      localStorage.setItem("pnc_user", JSON.stringify(data));
      setUser(data);
    }).catch(() => logout());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthCtx.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export function RequireAuth({ children, adminOnly = false }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/supervisor" replace />;
  return children;
}
